import type { TypedDocumentNode } from '@apollo/client';
import { gql } from '@apollo/client';
import type { CSSProperties } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { generatePath } from 'react-router';
import styled from 'styled-components';

import { Link } from '@sorare/routing';
import { Modal } from '@sorare/core/src/atoms/Modal/index.tsx';
import { Button } from '@sorare/core/src/atoms/buttons/Button/index.tsx';
import { IconButton } from '@sorare/core/src/atoms/buttons/IconButton/index.tsx';
import { iconX } from '@sorare/core/src/atoms/icons/system/iconX.ts';
import { Vertical } from '@sorare/core/src/atoms/layout/flex/index.tsx';
import { Backdrop } from '@sorare/core/src/atoms/loader/Backdrop/index.tsx';
import {
  BrandL,
  DisplayXS,
  HeadlineL,
  LabelS,
} from '@sorare/core/src/atoms/typography/index.tsx';
import { Trapezoid } from '@sorare/core/src/atoms/ui/Trapezoid/index.tsx';
import { RewardConfigText } from '@sorare/core/src/components/rewards/AnyRewardConfig/RewardConfigText/index.tsx';
import { BLOG_SPORT_SLUG } from '@sorare/core/src/constants/__generated__/routes.ts';
import { useBodyLock } from '@sorare/core/src/hooks/useBodyLock.ts';
import { useShortcut } from '@sorare/core/src/hooks/useShortcut.ts';
import { isTypeFn } from '@sorare/core/src/lib/gql/index.ts';

import backgroundUrl from '#src/components/series/GuessTheScore/assets/golden-score-bg.webp';
import logoUrl from '#src/components/series/GuessTheScore/assets/golden-score-logo.webp';
import paperRipBottomUrl from '#src/components/series/PickGoldenScore/assets/paper-rip-bottom.webp';

import type { GuessTheScoreIntroDialog_guessTheScoreTask } from './__generated__/index.graphql.ts';
import colorsLogo from './assets/colors-logo.svg';

const Wrapper = styled(Vertical).attrs({ gap: 2 })`
  position: relative;
  width: 100%;
  max-width: 343px;
  margin: auto;
  padding-top: calc(10 * var(--unit));
  overflow: hidden;
`;

const CloseButton = styled(IconButton)`
  position: absolute;
  top: 0;
  right: 0;
  z-index: 3;
`;

const JoinButton = styled(Button)`
  align-self: center;
`;

const Card = styled.div`
  position: relative;
`;

const Top = styled(Vertical).attrs({ center: true, gap: 0 })`
  position: relative;
  padding: var(--triple-unit) var(--double-unit) 0;
  isolation: isolate;
  &::before {
    z-index: -2;
    content: '';
    position: absolute;
    inset: 0;
    background: url(${backgroundUrl}) center / cover no-repeat;
    border-radius: var(--double-unit) 40px 0 0;
  }
`;

const PlayerImg = styled.img`
  position: absolute;
  bottom: 0;
  z-index: -1;
  width: 58%;
  height: 118%;
  object-fit: cover;
  object-position: top;

  &.left {
    left: -16%;
  }
  &.right {
    right: -16%;
  }
`;

const Logo = styled.img`
  width: 180px;
  height: auto;
  margin: calc(6 * var(--unit)) 0 calc(10 * var(--unit));
`;

const Ribbons = styled(Vertical).attrs({ center: true, gap: 0 })`
  top: calc(10 * var(--unit) - 20px);
  left: 0;
  right: 0;
  position: absolute;
  z-index: 1;
`;

const Sheet = styled(Vertical).attrs({ center: true, gap: 2 })`
  position: relative;
  z-index: 1;
  width: 100%;
  padding: var(--unit) var(--double-unit) var(--triple-unit);
  color: var(--c-black);
  text-align: center;
  isolation: isolate;
  &::before {
    z-index: -1;
    content: '';
    position: absolute;
    inset: -100% 0 0;
    background: url(${paperRipBottomUrl}) center top / cover no-repeat;
    border-radius: 0 0 var(--double-unit) 40px;
  }
`;

type Props = {
  open: boolean;
  task: GuessTheScoreIntroDialog_guessTheScoreTask;
  onClose: () => void;
  onJoin: () => void;
};

export const GuessTheScoreIntroDialog = ({
  open,
  task,
  onClose,
  onJoin,
}: Props) => {
  const { formatMessage } = useIntl();
  const jackpot = task.rewardConfigs.find(isTypeFn('MonetaryRewardConfig'));

  useBodyLock(open);
  useShortcut(
    'Escape',
    () => {
      if (open) {
        onClose();
      }
    },
    [open, onClose]
  );

  if (!open) {
    return null;
  }

  return (
    <Modal>
      <Backdrop blur>
        <Wrapper role="dialog" onClick={e => e.stopPropagation()}>
          <CloseButton
            icon={iconX}
            size="medium"
            color="transparent"
            aria-label={formatMessage({ defaultMessage: 'Close' })}
            onClick={onClose}
          />
          <Ribbons center gap={0}>
            <Trapezoid
              as="div"
              style={{ '--background': '#FF0000' } as CSSProperties}
            >
              <LabelS
                bold
                uppercase
                className="text-gradient-color"
                style={
                  {
                    '--text-gradient-color':
                      'linear-gradient(180deg, #FFF 2.66%, #FFEFAE 98.8%)',
                  } as CSSProperties
                }
              >
                <FormattedMessage defaultMessage="Play & win" />
              </LabelS>
            </Trapezoid>
            {!!jackpot && (
              <Trapezoid
                as="div"
                style={{ '--background': '#26210D' } as CSSProperties}
              >
                <BrandL
                  className="text-gradient-color"
                  style={
                    {
                      '--text-gradient-color':
                        'linear-gradient(180deg, #FFF 52.32%, #FFEFAE 85.44%)',
                    } as CSSProperties
                  }
                >
                  <RewardConfigText anyRewardConfig={jackpot} />
                </BrandL>
              </Trapezoid>
            )}
          </Ribbons>
          <Card>
            <Top>
              {!!task.leftPlayer && (
                <PlayerImg
                  className="left"
                  src={task.leftPlayer.fullPictureUrl}
                  alt=""
                  role="presentation"
                />
              )}
              {!!task.rightPlayer && (
                <PlayerImg
                  className="right"
                  src={task.rightPlayer.fullPictureUrl}
                  alt=""
                  role="presentation"
                />
              )}

              <Logo src={logoUrl} alt="Golden Score" />
            </Top>
            <Sheet>
              <div>
                <DisplayXS as="p">
                  <FormattedMessage defaultMessage="Guess your exact score" />
                </DisplayXS>
                {!!jackpot && (
                  <HeadlineL as="p">
                    <FormattedMessage
                      defaultMessage="Win {reward}!"
                      values={{
                        reward: <RewardConfigText anyRewardConfig={jackpot} />,
                      }}
                    />
                  </HeadlineL>
                )}
              </div>
              <img src={colorsLogo} alt="Sorare Colors" height={16} />
            </Sheet>
          </Card>
          <JoinButton color="primary" size="large" onClick={onJoin}>
            <FormattedMessage defaultMessage="Join the event" />
          </JoinButton>
          <Link
            to={generatePath(BLOG_SPORT_SLUG, {
              sport: 'football',
              slug: 'golden-score-raffles-terms-and-conditions',
            })}
            target="_blank"
            style={{ alignSelf: 'center' }}
          >
            <LabelS
              color="var(--c-white)"
              style={{ textDecoration: 'underline' }}
            >
              <FormattedMessage defaultMessage="Terms and conditions" />
            </LabelS>
          </Link>
        </Wrapper>
      </Backdrop>
    </Modal>
  );
};

GuessTheScoreIntroDialog.fragments = {
  guessTheScoreTask: gql`
    fragment GuessTheScoreIntroDialog_guessTheScoreTask on GuessTheScoreTask {
      id
      leftPlayer {
        slug
        fullPictureUrl
      }
      rightPlayer {
        slug
        fullPictureUrl
      }
      rewardConfigs {
        id
        ...RewardConfigText_anyRewardConfigInterface
      }
    }
    ${RewardConfigText.fragments.anyRewardConfigInterface}
  ` as TypedDocumentNode<GuessTheScoreIntroDialog_guessTheScoreTask>,
};
